import React from "react";
import { useGetMemberSavedCardsQuery } from "../../../../store/member/member-contribution";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import { formatMoney } from "../../../../utils";

/**
 * Payment Selection Component for Members
 * Shows saved/tokenized cards and option to pay with new card
 * Provides a user-friendly interface for payment method selection
 */
const MemberPaymentSelection = ({ selectedPaymentMethod, onSelectPaymentMethod, amount }) => {
  const { data: savedCards = [], isLoading: isLoadingCards } = useGetMemberSavedCardsQuery();

  // Payment method types
  const PAYMENT_TYPES = {
    NEW_CARD: "new_card",
    SAVED_CARD: "saved_card",
  };

  const handleSelectNewCard = () => {
    onSelectPaymentMethod({
      type: PAYMENT_TYPES.NEW_CARD,
      id: null,
      label: "Pay with New Card",
    });
  };

  const handleSelectSavedCard = (card) => {
    onSelectPaymentMethod({
      type: PAYMENT_TYPES.SAVED_CARD,
      id: card.id,
      card_token: card.token || card.authorization_code,
      label: `****${card.last4 || card.last_four_digits} (${card.brand || card.card_type})`,
      card: card,
    });
  };

  const formatCardNumber = (last4) => {
    return `****${last4}`;
  };

  const getCardIcon = (brand) => {
    const brandLower = brand?.toLowerCase() || "";
    if (brandLower.includes("visa")) return "💳";
    if (brandLower.includes("master")) return "💳";
    if (brandLower.includes("verve")) return "💳";
    return "💳";
  };

  return (
    <div className="space-y-4">
      <div>
        <h6 className="fw-semibold text-black1 mb-3">Select Payment Method</h6>
        <p className="text-muted small mb-4">
          Choose to pay with a saved card or add a new card
        </p>
      </div>

      {/* New Card Option - Always Available */}
      <div
        onClick={handleSelectNewCard}
        className={`border rounded-4 p-4 cursor-pointer transition-all ${
          selectedPaymentMethod?.type === PAYMENT_TYPES.NEW_CARD
            ? "border-primary1 bg-light"
            : "border-gray2 bg-white hover-border-primary1"
        }`}
        style={{
          borderWidth: "2px",
          borderColor:
            selectedPaymentMethod?.type === PAYMENT_TYPES.NEW_CARD
              ? "var(--color-primary)"
              : "#e5e5e5",
          backgroundColor:
            selectedPaymentMethod?.type === PAYMENT_TYPES.NEW_CARD
              ? "var(--color-primary-soft)"
              : "#ffffff",
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: "48px",
                height: "48px",
                backgroundColor: "var(--color-primary)",
                color: "white",
                fontSize: "20px",
              }}
            >
              +
            </div>
            <div>
              <h6 className="fw-semibold text-black1 mb-1">Pay with New Card</h6>
              <p className="text-muted small mb-0">
                Enter card details securely via Paystack
              </p>
            </div>
          </div>
                  {selectedPaymentMethod?.type === PAYMENT_TYPES.NEW_CARD && (
                    <div className="text-primary1">
                      <FallbackImage
                        src="/Icon/tick.svg"
                        width="24px"
                        height="24px"
                      />
                    </div>
                  )}
        </div>
      </div>

      {/* Saved Cards Section */}
      {isLoadingCards ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary1" role="status">
            <span className="visually-hidden">Loading saved cards...</span>
          </div>
        </div>
      ) : savedCards && savedCards.length > 0 ? (
        <div className="space-y-3">
          <h6 className="fw-semibold text-black1 mb-3">Saved Cards</h6>
          {savedCards.map((card) => {
            const isSelected =
              selectedPaymentMethod?.type === PAYMENT_TYPES.SAVED_CARD &&
              selectedPaymentMethod?.id === card.id;

            return (
              <div
                key={card.id}
                onClick={() => handleSelectSavedCard(card)}
                className={`border rounded-4 p-4 cursor-pointer transition-all ${
                  isSelected
                    ? "border-primary1 bg-light"
                    : "border-gray2 bg-white hover-border-primary1"
                }`}
                style={{
                  borderWidth: "2px",
                  borderColor: isSelected ? "var(--color-primary)" : "#e5e5e5",
                  backgroundColor: isSelected
                    ? "var(--color-primary-soft)"
                    : "#ffffff",
                }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "var(--color-secondary)",
                        color: "white",
                        fontSize: "20px",
                      }}
                    >
                      {getCardIcon(card.brand || card.card_type)}
                    </div>
                    <div>
                      <h6 className="fw-semibold text-black1 mb-1">
                        {formatCardNumber(
                          card.last4 || card.last_four_digits || "0000"
                        )}
                      </h6>
                      <p className="text-muted small mb-0">
                        {card.brand || card.card_type || "Card"} • Expires{" "}
                        {card.exp_month || "MM"}/{card.exp_year || "YY"}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="text-primary1">
                      <FallbackImage
                        src="/Icon/tick.svg"
                        width="24px"
                        height="24px"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border rounded-4 p-4 bg-gray3 text-center">
          <p className="text-muted small mb-0">
            No saved cards. You can save your card after this payment for faster
            checkout next time.
          </p>
        </div>
      )}

      {/* Amount Summary */}
      {amount > 0 && (
        <div
          className="border rounded-4 p-4 mt-4"
          style={{
            backgroundColor: "var(--color-secondary-soft)",
            borderColor: "var(--color-secondary)",
            borderWidth: "2px",
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <span className="fw-semibold text-black1">Total Amount</span>
            <span className="fw-bold text-primary1" style={{ fontSize: "20px" }}>
              {formatMoney(amount)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberPaymentSelection;

